import { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  Box,
  Pagination,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { formatDuration, getFirstLetter } from "../../../utils/common";
import { jwtDecode } from "jwt-decode";
import NoData from "../../../components/NoData";
import { useNavigate } from "react-router-dom";
import { palette, theme as colorTheme } from "../../../Convoze.theme";



const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  border: "1px solid #e0e0e0",
  [theme.breakpoints.down("sm")]: {
    "& .MuiTable-root": {
      minWidth: "auto",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "1px solid #f5f5f5",
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: "#fafafa",
  "& .MuiTableCell-head": {
    fontWeight: 600,
    color: colorTheme('palette', 'secondary').value,
    fontSize: "0.875rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
}));

const RankCell = styled(TableCell)(
  ({ theme, isCurrentUser, highestScore }) => ({
    borderLeft: isCurrentUser
      // ? "4px solid #6366F1"
      ? `4px solid ${colorTheme('palette', 'primary').value}`
      : highestScore
        ? `4px solid ${colorTheme('palette', 'neutral').value}`
        : "4px solid #E5E7EB",
    fontWeight: "bold",
    fontSize: "1.1rem",
    color: colorTheme('palette', 'primary').value,
  })
);

const CurrentUserRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#f8f9ff",
  "&:hover": {
    backgroundColor: "#f0f2ff",
  },
}));
const TopUserRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#FEFCE8",
  "&:hover": {
    backgroundColor: "#f0f2ff",
  },
}));

const DashboardCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  border: "1px solid #e0e0e0",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    margin: theme.spacing(0, 1),
  },
}));

function TeamRanking({ leaderBoardData, isCurrentAgent }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : "";
  const teamData = [
    {
      rank: 1,
      name: "Alex Chen",
      department: "Technical Support",
      avatar: "/placeholder.svg?height=40&width=40",
      overallScore: 96,
      sentiment: "9.2/10",
      resolutionRate: "94%",
      csat: "4.8/5",
      trend: "up",
      isCurrentUser: false,
      isTopScore: true,
    },
    {
      rank: 2,
      name: "Sarah Johnson",
      department: "Customer Support",
      avatar: "/placeholder.svg?height=40&width=40",
      overallScore: 93,
      sentiment: "9.0/10",
      resolutionRate: "92%",
      csat: "4.7/5",
      trend: "neutral",
      isCurrentUser: false,
      isTopScore: false,
    },
    {
      rank: 3,
      name: "David Kim",
      department: "Technical Support",
      avatar: "/placeholder.svg?height=40&width=40",
      overallScore: 88,
      sentiment: "8.5/10",
      resolutionRate: "89%",
      csat: "4.5/5",
      trend: "up",
      isCurrentUser: false,
      isTopScore: false,
    },
    {
      rank: 4,
      name: "John Smith (You)",
      department: "Customer Support",
      avatar: "/placeholder.svg?height=40&width=40",
      overallScore: 87,
      sentiment: "8.3/10",
      resolutionRate: "87%",
      csat: "4.4/5",
      trend: "up",
      isCurrentUser: true,
      isTopScore: false,
    },
    {
      rank: 5,
      name: "Emma Wilson",
      department: "Customer Support",
      avatar: "/placeholder.svg?height=40&width=40",
      overallScore: 84,
      sentiment: "8.2/10",
      resolutionRate: "85%",
      csat: "4.2/5",
      trend: "down",
      isCurrentUser: false,
      isTopScore: false,
    },
    {
      rank: 6,
      name: "Michael Brown",
      department: "Technical Support",
      avatar: "/placeholder.svg?height=40&width=40",
      overallScore: 81,
      sentiment: "7.9/10",
      resolutionRate: "83%",
      csat: "4.0/5",
      trend: "neutral",
      isCurrentUser: false,
      isTopScore: false,
    },
  ];

  const getTrendIcon = (trend) => {
    switch (trend) {
      case true:
        return <TrendingUpIcon sx={{ color: colorTheme('palette', 'positive').value, fontSize: 20 }} />;
      case false:
        return <TrendingDownIcon sx={{ color: colorTheme('palette', 'negative').value, fontSize: 20 }} />;
      default:
        return <RemoveIcon sx={{ color: colorTheme('palette', 'secondary').value, fontSize: 20 }} />;
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate paginated data
  const paginatedData = leaderBoardData?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <DashboardCard elevation={0}>
      {/* <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          fontWeight="600"
          color="text.primary"
          sx={{ fontSize: "1.125rem" }}
        >
          Team Ranking
        </Typography>
        <InfoIcon sx={{ fontSize: 16,color:"#6366F1" }} />
      </Box> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: 3,
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          fontWeight="600"
          color="text.primary"
          sx={{ fontSize: "1.125rem" }}
        >
          Team Ranking
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1 }}
        >
          Team Standings – Live Leaderboard
        </Typography>
      </Box>

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <StyledTableHead sx={{ whiteSpace: "nowrap" }}>
            <TableRow>
              <StyledTableCell>RANK</StyledTableCell>
              <StyledTableCell>AGENT</StyledTableCell>
              {!isMobile && <StyledTableCell>OVERALL SCORE</StyledTableCell>}
              {!isMobile && <StyledTableCell>SENTIMENT</StyledTableCell>}
              {!isTablet && (
                <StyledTableCell>
                  {decodedToken.processType === "sales"
                    ? "CONVERSION RATE"
                    : "RESOLUTION RATE"}
                </StyledTableCell>
              )}
              {!isTablet && <StyledTableCell>CSAT</StyledTableCell>}
              {!isTablet && <StyledTableCell>AHT</StyledTableCell>}
              <StyledTableCell>TREND</StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {paginatedData?.length == 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              paginatedData?.map((member) => {
                const RowComponent =
                  member?.agentId == isCurrentAgent?.agentId
                    ? CurrentUserRow
                    : member?.rank == 1
                      ? TopUserRow
                      : TableRow;


                const g =
                  member.gender ??
                  ["male", "female", "other"][
                  (member.agentFullName || "Agent")
                    .split("")
                    .reduce((h, c) => h + c.charCodeAt(0), 0) % 3
                  ];


                const avatarUrl =
                  g === "male"
                    ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(
                      member.agentFullName || "Agent"
                    )}&sex=male&radius=50&backgroundColor=f8f8f8`
                    : g === "female"
                      ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(
                        member.agentFullName || "Agent"
                      )}&sex=female&radius=50&backgroundColor=f8f8f8`
                      : `https://api.dicebear.com/7.x/personas/svg?seed=${encodeURIComponent(
                        member.agentFullName || "Agent"
                      )}&radius=50&backgroundColor=f8f8f8`;



                return (
                  <RowComponent key={member.rank}>
                    <RankCell
                      isCurrentUser={member?.agentId == isCurrentAgent?.agentId}
                      highestScore={member?.rank == 1}
                    >
                      {member?.rank}
                    </RankCell>
                    <StyledTableCell
                      sx={{
                        "&:hover": {
                          // color: "#1976d2",
                          color: colorTheme('palette', 'primary').value
                        },
                      }}
                      onClick={() => {
                        // const avatar = `https://api.dicebear.com/7.x/personas/svg?seed=${encodeURIComponent(
                        //   member?.agentFullName || member.rank
                        // )}&radius=50&backgroundColor=f8f8f8`;

                        // const name = member?.agentFullName || "";
                        // navigate(
                        //   `/user/agent-profile/${
                        //     member?.agentId
                        //   }?avatar=${encodeURIComponent(
                        //     avatar
                        //   )}&agentName=${encodeURIComponent(name)}`
                        // )


                        navigate(
                          `/user/agent-profile/${member.agentId}?avatar=${encodeURIComponent(
                            avatarUrl
                          )}&agentName=${encodeURIComponent(member.agentFullName || '')}`
                        )

                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          cursor: "pointer",
                        }}
                      >
                        <Avatar
                          // src={`https://api.dicebear.com/7.x/personas/svg?seed=${encodeURIComponent(
                          //   member?.agentFullName || member?.rank
                          // )}&radius=50&backgroundColor=f8f8f8`}
                          src={avatarUrl}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            color="text.primary"
                          >
                            {member?.agentFullName || "Agent Name"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member?.agentId || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </StyledTableCell>
                    {!isMobile && (
                      <StyledTableCell>
                        <Typography variant="body2" fontWeight="600">
                          {member?.averagePerformance
                            ? Math.round(member?.averagePerformance)
                            : 0}
                        </Typography>
                      </StyledTableCell>
                    )}
                    {!isMobile && (
                      <StyledTableCell>
                        <Typography variant="body2">
                          {member?.averageSentimentScore
                            ? `${member?.averageSentimentScore}%`
                            : 0}
                        </Typography>
                      </StyledTableCell>
                    )}
                    {!isTablet && (
                      <StyledTableCell>
                        <Typography variant="body2">
                          {decodedToken.companyId === "67e560a6ae2e0d3f96b5eb08"
                            ? `${member?.conversionRate ?? 0}%`
                            : `${member?.resolutionRate ?? 0}%`}
                        </Typography>
                      </StyledTableCell>
                    )}
                    {!isTablet && (
                      <StyledTableCell>
                        <Typography variant="body2">
                          {member?.averageCsatScoreOutOf5
                            ? `${member?.averageCsatScoreOutOf5}%`
                            : 0}
                        </Typography>
                      </StyledTableCell>
                    )}
                    {!isTablet && (
                      <StyledTableCell>
                        <Typography variant="body2">
                          {member?.formattedHandlingTime
                            ? formatDuration(member?.formattedHandlingTime)
                            : "0"}
                        </Typography>
                      </StyledTableCell>
                    )}
                    <StyledTableCell>
                      {getTrendIcon(member?.trend?.overallTrend)}
                    </StyledTableCell>
                  </RowComponent>
                );
              })
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {(page - 1) * rowsPerPage + 1} to{" "}
          {Math.min(page * rowsPerPage, leaderBoardData?.length)} of{" "}
          {leaderBoardData?.length} team members
        </Typography>

        <Pagination
          count={Math.ceil(leaderBoardData?.length / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          size={isMobile ? "small" : "medium"}
          sx={{
            "& .MuiPaginationItem-root": {
              // color: "#6366F1",
              color: colorTheme('palette', 'primary').value
            },
            "& .MuiPaginationItem-root.Mui-selected": {
              // backgroundColor: "#6366F1",
              backgroundColor: colorTheme('palette', 'primary').value,
              color: "#fff",
              // borderColor: "#6366F1",
              borderColor: colorTheme('palette', 'primary').value,
              "&:hover": {
                // backgroundColor: "#4F52D1",
                backgroundColor: colorTheme('palette', 'primary').value
              },
            },
          }}
        />
      </Box>
    </DashboardCard>
  );
}

export default TeamRanking;
